import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MapPin, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type AccountAddress = {
  latitude?: string | number | null;
  longitude?: string | number | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
};

export function hasGeocodedAddress(account: AccountAddress | undefined): boolean {
  if (!account) return false;
  const lat = account.latitude == null ? 0 : Number(account.latitude);
  const lng = account.longitude == null ? 0 : Number(account.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0;
}

type Props = {
  onSaved: () => void;
  onCancel?: () => void;
  initialError?: string | null;
  title?: string;
  description?: string;
};

export function SellerAddressFixer({
  onSaved,
  onCancel,
  initialError,
  title = "Add your pickup address",
  description = "Buyers see how close you are and community delivery uses this to route a driver. It's only shared with buyers after they checkout.",
}: Props) {
  const queryClient = useQueryClient();

  const { data: account } = useQuery<AccountAddress>({
    queryKey: ["/api/account"],
    queryFn: async () => {
      const res = await fetch("/api/account", { credentials: "include" });
      if (!res.ok) throw new Error("Could not load your account");
      return res.json();
    },
  });

  const [line1, setLine1] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [error, setError] = useState<string | null>(initialError ?? null);
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    if (prefilled || !account) return;
    setLine1(String(account.addressLine1 ?? ""));
    setCity(String(account.city ?? ""));
    setState(String(account.state ?? ""));
    setZip(String(account.zipCode ?? ""));
    setPrefilled(true);
  }, [account, prefilled]);

  const save = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/account/profile", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          addressLine1: line1.trim(),
          city: city.trim(),
          state: state.trim().toUpperCase().slice(0, 2),
          zipCode: zip.trim(),
        }),
      });
      if (!res.ok) throw new Error((await res.text()) || "Could not save address");
      return res.json() as Promise<AccountAddress & { geocodeStatus?: string }>;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(["/api/account"], updated);
      queryClient.invalidateQueries({ queryKey: ["/api/account"] });
      if (hasGeocodedAddress(updated)) {
        setError(null);
        onSaved();
      } else {
        setError(
          "We couldn't pin that address on a map. Double-check the spelling, or try a more specific street.",
        );
      }
    },
    onError: (err: Error) => setError(err.message),
  });

  const canSubmit =
    line1.trim().length > 0 &&
    city.trim().length > 0 &&
    state.trim().length > 0 &&
    zip.trim().length > 0 &&
    !save.isPending;

  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <MapPin className="h-7 w-7 text-primary" />
        </div>
        <h2 className="text-xl font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      <div className="space-y-3">
        <div className="space-y-1">
          <Label className="text-xs">Street address</Label>
          <Input
            value={line1}
            onChange={(e) => setLine1(e.target.value)}
            placeholder="123 Main St"
            autoComplete="address-line1"
          />
        </div>
        <div className="grid grid-cols-5 gap-2">
          <div className="col-span-2 space-y-1">
            <Label className="text-xs">City</Label>
            <Input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              autoComplete="address-level2"
            />
          </div>
          <div className="col-span-1 space-y-1">
            <Label className="text-xs">State</Label>
            <Input
              value={state}
              onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
              placeholder="NY"
              maxLength={2}
              autoComplete="address-level1"
            />
          </div>
          <div className="col-span-2 space-y-1">
            <Label className="text-xs">ZIP</Label>
            <Input
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              maxLength={10}
              autoComplete="postal-code"
            />
          </div>
        </div>

        {error && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
            {error}
          </div>
        )}
      </div>

      <div className="flex gap-2">
        {onCancel && (
          <Button variant="ghost" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
        )}
        <Button
          onClick={() => save.mutate()}
          disabled={!canSubmit}
          className="flex-1"
        >
          {save.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <ChevronRight className="mr-2 h-4 w-4" />
          )}
          Save & continue
        </Button>
      </div>
    </div>
  );
}
